import { GridSortItem } from "@mui/x-data-grid";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useRef } from "react";
import { useEncounterCreationGate } from "../hooks/use-encounter-creation-gate";
import { ProviderControllerApi } from "../services";
import { ProviderListItem } from "../types/provider-types";
import {
  DGT_Pagination,
  asPaginatedContent,
  getConfigObject,
  getPageIndexWhileSearch,
  getPageSize,
  useBaseContent,
} from "../utils/utils";

export interface UseProvidersReferralListProps {
  pagination: DGT_Pagination;
  gridSorting?: GridSortItem[];
  searchString?: string;
  xTENANTID?: string;
  isReferral?: boolean;
  status?: boolean;
  archive?: boolean;
  enabled?: boolean;
}

/**
 * Shared implementation for the lightweight provider-dropdown api.
 * `isReferral` is forwarded as-is: pass `undefined` to omit the filter.
 */
function useProviderListV2Base({
  gridSorting,
  pagination,
  searchString,
  xTENANTID,
  isReferral,
  status,
  archive,
  enabled = true,
}: UseProvidersReferralListProps): useBaseContent<ProviderListItem> {
  const prevPage = useRef<number>(pagination.page);

  const pageIndex = getPageIndexWhileSearch({
    pagination,
    searchKey: searchString,
    prevPage,
  });
  const pageSize = getPageSize(searchString, pagination);
  const skip = useEncounterCreationGate();

  return useQuery({
    queryKey: [
      "providers-referral-list",
      pageSize,
      searchString,
      pageIndex,
      xTENANTID,
      isReferral,
      gridSorting,
      status,
      archive,
    ],
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    enabled: !!xTENANTID && enabled && !skip,
    queryFn: async () => {
      return (
        new ProviderControllerApi(getConfigObject())
          // page?: number, size?: number, sortBy?: string, sortDirection?: string, searchString?: string, isReferral?: boolean, status?: boolean, archive?: boolean, xTENANTID?: string, options?: RawAxiosRequestConfig
          .getAllProvidersNameAndId(
            pageIndex, //page
            pageSize, //size
            undefined,
            undefined,
            searchString,
            isReferral,
            status,
            archive,
            xTENANTID
          )
          .then((res) => asPaginatedContent<ProviderListItem>(res?.data))
      );
    },
  });
}

/**
 * @author [Rajkumar Rathod]
 *
 * @description - A lightweight api to use for provider dropdown.
 * Defaults `isReferral` to `false` when not provided.
 */
export function useProviderListV2(
  props: UseProvidersReferralListProps
): useBaseContent<ProviderListItem> {
  return useProviderListV2Base({
    ...props,
    isReferral: props.isReferral ?? false,
  });
}

/**
 * @description - Same as {@link useProviderListV2} but sends `isReferral` as
 * `undefined` (param omitted) so the list is not filtered by referral status.
 */
export function useProviderListV2WithoutReferralFilter(
  props: Omit<UseProvidersReferralListProps, "isReferral">
): useBaseContent<ProviderListItem> {
  return useProviderListV2Base({ ...props, isReferral: undefined });
}
